import { useState } from 'react';
import type { RunTestsResult, TestRunUiStatus } from '../types';

type Test = NonNullable<RunTestsResult['fileResults'][number]['tests']>[number];

/**
 * Per-test row inside the chat trail's expandable run-tests panel.
 *
 * Renders one of six visual states keyed off the streamed status:
 *   - pending : queued, never started (no spinner — just a faint dot)
 *   - running : currently executing (spinner)
 *   - passed  : green ✓
 *   - failed  : red ✗ with an expandable assertion message
 *   - error   : red ✗ (test threw, not an assertion mismatch)
 *   - skipped : muted ○
 *
 * Failures auto-expand the assertion message so the user sees what broke
 * without an extra click. Passes stay collapsed since the message is just
 * the test name.
 */
export function RunTestsTestRow({ test }: { test: Test }) {
  const status: TestRunUiStatus =
    (test.status as TestRunUiStatus | undefined) ??
    (test.passed ? 'passed' : 'failed');
  const isFailure = status === 'failed' || status === 'error';
  // The row is "detailed" (clickable + expandable) when there's anything
  // to show beyond the test name itself: an assertion message OR a
  // resolved source snippet from the stack trace.
  const hasDetail = isFailure && (!!test.failureMessage || !!test.failureSource);
  // Failures default to expanded — that's the whole point of the click-through.
  const [open, setOpen] = useState<boolean>(hasDetail);

  const icon =
    status === 'running'
      ? '◐'
      : status === 'pending'
        ? '○'
        : status === 'skipped'
          ? '○'
          : status === 'passed'
            ? '✓'
            : '✗';
  const iconClass =
    status === 'running'
      ? 'test-status running'
      : status === 'pending'
        ? 'test-status pending'
        : status === 'skipped'
          ? 'test-status skipped'
          : status === 'passed'
            ? 'test-status pass'
            : 'test-status fail';

  return (
    <div className="pipeline-test-row" data-test-status={status}>
      <div
        className={`pipeline-test-row-header${hasDetail ? ' clickable' : ''}`}
        onClick={hasDetail ? () => setOpen(!open) : undefined}
        style={hasDetail ? { cursor: 'pointer' } : undefined}
      >
        {hasDetail && (
          <span className="step-expand-icon">{open ? '▾' : '▸'}</span>
        )}
        <span
          className={iconClass}
          // The spinner-style status uses a CSS animation hook keyed off
          // data-spinning; falling back to the static char keeps the
          // accessibility tree readable when styles fail to load.
          data-spinning={status === 'running' ? 'true' : undefined}
        >
          {icon}
        </span>
        <span className="pipeline-test-name">{test.fullName ?? test.name}</span>
        {typeof test.durationMs === 'number' && status !== 'running' && status !== 'pending' && (
          <span className="pipeline-test-duration">
            {Math.round(test.durationMs)}ms
          </span>
        )}
      </div>
      {open && hasDetail && (
        <>
          {test.failureMessage && (
            <pre className="pipeline-test-failure">{test.failureMessage}</pre>
          )}
          {test.failureSource && (
            <div className="pipeline-test-failure-source">
              <div className="pipeline-test-failure-source-label">
                {test.failureSource.file}:{test.failureSource.line}
                {typeof test.failureSource.column === 'number'
                  ? `:${test.failureSource.column}`
                  : ''}
              </div>
              <pre className="pipeline-test-failure-source-snippet">
                {test.failureSource.snippet}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );
}
